import { Typography, Button } from '@mui/material';
import { EmbedWorkflowConfiguration, Workflow, WorkflowNode, WorkflowNodeType } from 'api/model';
import { useState } from 'react';
import { NavigateFunction } from 'react-router';
import { toast, ToastContentProps } from 'react-toastify';
import PersistentNodesToast from './PersistentNodesToast';
import useRevisedWorkflowsFromNodes from 'api/workflows/useRevisedWorkflowsFromNodes';
import { getRevisedNodesMapping, updateWorkflow } from 'api/workflows/requests';
import useProjects from 'api/projects/useProjects';
import WorkflowCard from './WorkflowCard';
import useWorkflows from 'api/workflows/useWorkflows';
import { useDrawer } from 'hooks/useDrawer';

const NODE_TYPES_WITH_PROMPT_INPUTS: WorkflowNodeType[] = [
  'llm_prompt',
  'llm_research',
  'data_loader_llm_research',
  'llm_map_reduce',
];

export const PROMPT_REVISION_FIELD = 'prompt_revision';
export const EVALUATION_PROMPT_REVISION_FIELD = 'evaluation_prompt_revision';
export const FUNCTION_REVISION_FIELD = 'function_revision';
export const WORKFLOW_REVISION_FIELD = 'revision';

export type RevisionField =
  | typeof PROMPT_REVISION_FIELD
  | typeof EVALUATION_PROMPT_REVISION_FIELD
  | typeof FUNCTION_REVISION_FIELD
  | typeof WORKFLOW_REVISION_FIELD;

type Props = Partial<ToastContentProps> & {
  title: string;
  nodes: WorkflowNode[];
  revision: number;
  revisionField: (node: WorkflowNode) => RevisionField;
  newPromptInputs?: string[];
  removedPromptInputs?: string[];
  activeWorkflowId?: string;
  navigate: NavigateFunction;
};

const OutdatedRevisionsToast = (props: Props) => {
  const { title, nodes, navigate, revision, revisionField, newPromptInputs, removedPromptInputs } = props;
  const nodeIds = new Set(nodes.map((node) => node.id));
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(() => new Set(nodeIds));
  const { projects } = useProjects();
  const { clearQueryKey } = useWorkflows({ enabled: false });
  const { revisedWorkflowsData: workflows } = useRevisedWorkflowsFromNodes({ nodes });
  const { close: closeDrawer, isNodeFormOpen } = useDrawer();

  const handleNodeSelectionChange = (nodeIds: string[], selected: boolean) => {
    setSelectedNodes((prev) => {
      const newSet = new Set(prev);

      if (selected) {
        nodeIds.forEach((id) => newSet.add(id));
      } else {
        nodeIds.forEach((id) => newSet.delete(id));
      }
      return newSet;
    });
  };

  const navigateToWorkflow = (workflow: Workflow) => {
    void navigate(`/projects/${workflow.project_id}/workflows/${workflow.id}/canvas`);
  };

  const updateSelectedNodes = async () => {
    const updatedWorkflowIds = new Set<string>();
    const updatedNodes: WorkflowNode[] = [];
    const embedWorkflowNodesRequiringUpdate: WorkflowNode[] = [];
    for (const workflow of workflows ?? []) {
      if (!workflow.nodes.some((node) => selectedNodes.has(node.id))) {
        continue;
      }

      const updatedWorkflow = {
        ...workflow,
        nodes: await Promise.all(
          workflow.nodes.map(async (node) => {
            if (!selectedNodes.has(node.id)) {
              return node;
            }

            const configuration = node.configuration;
            if ('input_data' in configuration && removedPromptInputs && removedPromptInputs.length > 0) {
              const inputData = configuration.input_data;
              if (inputData) {
                for (const input of removedPromptInputs) {
                  if (input in inputData) {
                    delete inputData[input];
                  }
                }
              }
            }

            if (node.type === 'embed_workflow') {
              const embedConfig = configuration as EmbedWorkflowConfiguration;
              const nodeIds = embedConfig.input_node_mappings
                .map((mapping) => mapping.child_node_id)
                .concat(embedConfig.output_node_mappings.map((mapping) => mapping.child_node_id));

              try {
                const sourceRevision = embedConfig.revision!;
                const targetRevision = revision;
                const mappingResponse = await getRevisedNodesMapping(
                  embedConfig.workflow_id,
                  sourceRevision,
                  targetRevision,
                  nodeIds,
                );
                const revisedMapping = mappingResponse.mapping;

                const isValidMapping = Object.values(revisedMapping).every((node) => node !== null);
                if (!isValidMapping) {
                  embedWorkflowNodesRequiringUpdate.push(node);
                  return node;
                } else {
                  embedConfig.input_node_mappings = embedConfig.input_node_mappings.map((mapping) => ({
                    ...mapping,
                    child_node_id: revisedMapping[mapping.child_node_id]!.id,
                  }));
                  embedConfig.output_node_mappings = embedConfig.output_node_mappings.map((mapping) => ({
                    ...mapping,
                    child_node_id: revisedMapping[mapping.child_node_id]!.id,
                  }));
                }
              } catch {
                embedWorkflowNodesRequiringUpdate.push(node);
                return node;
              }
            }

            const updatedNode = {
              ...node,
              configuration: {
                ...configuration,
                [revisionField(node)]: revision,
              },
            };
            updatedNodes.push(updatedNode);
            return updatedNode;
          }),
        ),
      };
      await updateWorkflow(updatedWorkflow.project_id, updatedWorkflow.id, updatedWorkflow);
      updatedWorkflowIds.add(updatedWorkflow.id);
    }
    clearQueryKey();
    if (isNodeFormOpen && props.activeWorkflowId && updatedWorkflowIds.has(props.activeWorkflowId)) {
      closeDrawer();
    }
    props.closeToast?.();
    toast.success(`Updated ${updatedNodes.length} node(s) to latest revision`);

    if (newPromptInputs && newPromptInputs.length > 0) {
      const parentNodesWithPromptInputs = updatedNodes.filter((node) =>
        NODE_TYPES_WITH_PROMPT_INPUTS.includes(node.type),
      );
      if (parentNodesWithPromptInputs.length > 0) {
        toast.warning(
          <PersistentNodesToast
            title={`New prompt inputs (${newPromptInputs.join(', ')}) must be manually selected in the following nodes:`}
            nodes={parentNodesWithPromptInputs}
            navigate={navigate}
          />,
          {
            autoClose: false,
            closeOnClick: false,
          },
        );
      }
    }

    if (embedWorkflowNodesRequiringUpdate.length > 0) {
      toast.warning(
        <PersistentNodesToast
          title="The following embed workflow nodes require manual updates due to incompatible changes:"
          nodes={embedWorkflowNodesRequiringUpdate}
          navigate={navigate}
        />,
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
    }
  };

  return (
    <div>
      <Typography className="mb-2">{title}</Typography>
      <div className="max-h-96 overflow-y-auto">
        {workflows?.map((workflow) => {
          const project = projects?.find((p) => p.id === workflow.project_id);
          return (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              project={project}
              nodeIds={nodeIds}
              onNavigate={navigateToWorkflow}
              selectable={true}
              selectedNodes={selectedNodes}
              onNodeSelectionChange={handleNodeSelectionChange}
            />
          );
        })}
      </div>
      <div className="flex flex-col items-start gap-1">
        <Button variant="contained" onClick={() => void updateSelectedNodes()} disabled={selectedNodes.size === 0}>
          Update Selected Nodes ({selectedNodes.size})
        </Button>
        <Typography variant="caption" color="textSecondary">
          This will overwrite any unsaved changes in the workflows being updated.
        </Typography>
      </div>
    </div>
  );
};

export default OutdatedRevisionsToast;
